import { Controller } from "@hotwired/stimulus"

// Commands are explicit message prefixes, parsed again on the server. They
// apply only to Jane, never to a human/channel destination in the messenger.
export default class extends Controller {
    static targets = ["menu", "option"]

    connect() {
        this.index = 0
        this.outside = event => { if (event.target !== this.field && !this.menuTarget.contains(event.target)) this.close() }
        document.addEventListener("click", this.outside)
        this.menuTarget.id ||= `composer-commands-${crypto.randomUUID()}`
    }

    disconnect() { document.removeEventListener("click", this.outside) }
    get field() { return this.element.querySelector('[data-assistant-compose-target="field"]') }

    suggest() {
        const field = this.field
        const match = field.name === "message" && field.value.match(/^\/([a-z-]*)$/i)
        if (!match) { this.close(); return }
        this.optionTargets.forEach(option => {
            const names = [option.dataset.command, ...(option.dataset.commandAliases || "").split(" ")]
            option.hidden = !names.some(name => name.startsWith(match[1].toLowerCase()))
        })
        this.index = 0
        if (!this.visible.length) { this.close(); return }
        this.menuTarget.hidden = false
        if (this.menuTarget.showPopover && !this.menuTarget.matches(":popover-open")) this.menuTarget.showPopover()
        const anchor = field.getBoundingClientRect()
        const width = Math.min(310, window.innerWidth - 24)
        Object.assign(this.menuTarget.style, { position: "fixed", margin: "0", inset: "auto", width: `${width}px` })
        const height = this.menuTarget.getBoundingClientRect().height
        this.menuTarget.style.left = `${Math.max(12, Math.min(anchor.left, window.innerWidth - width - 12))}px`
        this.menuTarget.style.top = `${anchor.top > height + 12 ? anchor.top - height - 6 : Math.min(anchor.bottom + 6, window.innerHeight - height - 12)}px`
        field.setAttribute("role", "combobox")
        field.setAttribute("aria-autocomplete", "list")
        field.setAttribute("aria-haspopup", "listbox")
        field.setAttribute("aria-controls", this.menuTarget.id)
        field.setAttribute("aria-expanded", "true")
        this.highlight()
    }

    get visible() { return this.optionTargets.filter(option => !option.hidden) }
    highlight() {
        this.visible.forEach((option, index) => {
            option.id ||= `${this.menuTarget.id}-${index}`
            option.setAttribute("aria-selected", String(index === this.index))
        })
        this.field.setAttribute("aria-activedescendant", this.visible[this.index].id)
    }

    keydown(event) {
        if (event.isComposing || this.menuTarget.hidden) return
        if (!["ArrowDown", "ArrowUp", "Enter", "Escape", "Tab"].includes(event.key)) return
        if (event.key === "Tab") { this.close(); return }
        event.preventDefault()
        event.stopImmediatePropagation()
        if (event.key === "Escape") { this.close(); return }
        if (event.key === "Enter") { this.choose({ currentTarget: this.visible[this.index] }); return }
        this.index = (this.index + (event.key === "ArrowDown" ? 1 : -1) + this.visible.length) % this.visible.length
        this.highlight()
    }

    choose(event) {
        if (!event.currentTarget) return
        this.field.value = `/${event.currentTarget.dataset.command} `
        this.close()
        this.field.focus()
        this.field.dispatchEvent(new Event("input", { bubbles: true }))
    }

    close() {
        if (this.menuTarget.matches(":popover-open")) this.menuTarget.hidePopover()
        this.menuTarget.hidden = true
        this.field?.setAttribute("aria-expanded", "false")
        this.field?.removeAttribute("aria-activedescendant")
        this.field?.removeAttribute("role")
    }
}
